import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Shield, CheckCircle, AlertTriangle, FileSignature } from 'lucide-react';
import { toast } from 'sonner';

export default function OnboardingPage() {
  const { user, updateProfile, authorize, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan');
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Profile data
  const [profile, setProfile] = useState({
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    ssn_last4: '',
    date_of_birth: ''
  });
  
  // Authorization data
  const [authData, setAuthData] = useState({
    consent_checkbox: false,
    e_signature: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleProfileChange = (e) => {
    setProfile(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(profile);
      toast.success('Profile updated!');
      setStep(2);
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    
    if (!authData.consent_checkbox) {
      toast.error('Please check the consent checkbox');
      return;
    }
    
    if (!authData.e_signature) {
      toast.error('Please provide your e-signature');
      return;
    }
    
    setLoading(true);
    try {
      await authorize({
        consent_checkbox: authData.consent_checkbox,
        e_signature: authData.e_signature,
        ip_address: 'client', // In production, get actual IP
        plan_selected: planFromUrl || 'free'
      });
      toast.success('Authorization complete!');
      setStep(3);
    } catch (err) {
      toast.error('Authorization failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = async (plan) => {
    if (plan === 'free') {
      toast.success('Welcome to AICreditPilot!');
      navigate('/dashboard');
      return;
    }
    
    setLoading(true);
    try {
      const { url } = await paymentsApi.createCheckout(plan, window.location.origin);
      window.location.href = url;
    } catch (err) {
      toast.error('Failed to start checkout');
      setLoading(false);
    }
  };

  const progressPercent = (step / 3) * 100;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-10 w-10 text-teal-600" />
            <span className="font-bold text-2xl text-slate-900" style={{ fontFamily: 'Manrope' }}>
              AICreditPilot
            </span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Complete Your Setup
          </h1>
          <p className="text-slate-600">Step {step} of 3</p>
        </div>

        {/* Progress */}
        <Progress value={progressPercent} className="h-2 mb-8" />

        {/* Step 1: Profile */}
        {step === 1 && (
          <Card className="border-slate-200 shadow-lg" data-testid="profile-step">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <span className="w-8 h-8 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-sm font-bold">1</span>
                Complete Your Profile
              </CardTitle>
              <CardDescription>
                We need a few details to personalize your experience.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={profile.phone}
                      onChange={handleProfileChange}
                      className="h-11"
                      data-testid="phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      name="date_of_birth"
                      type="date"
                      value={profile.date_of_birth}
                      onChange={handleProfileChange}
                      className="h-11"
                      data-testid="dob-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    name="address"
                    placeholder="123 Main St"
                    value={profile.address}
                    onChange={handleProfileChange}
                    className="h-11"
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      name="city"
                      placeholder="New York"
                      value={profile.city}
                      onChange={handleProfileChange}
                      className="h-11"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      name="state"
                      placeholder="NY"
                      value={profile.state}
                      onChange={handleProfileChange}
                      className="h-11"
                      data-testid="state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      placeholder="10001"
                      value={profile.zip_code}
                      onChange={handleProfileChange}
                      className="h-11"
                      data-testid="zip-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ssn_last4">Last 4 of SSN (Optional)</Label>
                  <Input
                    id="ssn_last4"
                    name="ssn_last4"
                    placeholder="1234"
                    maxLength={4}
                    value={profile.ssn_last4}
                    onChange={handleProfileChange}
                    className="h-11 w-32"
                    data-testid="ssn-input"
                  />
                  <p className="text-xs text-slate-500">Used for identity verification with bureaus</p>
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-teal h-11"
                  disabled={loading}
                  data-testid="profile-submit-btn"
                >
                  {loading ? 'Saving...' : 'Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Authorization */}
        {step === 2 && (
          <Card className="border-slate-200 shadow-lg" data-testid="authorization-step">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <span className="w-8 h-8 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-sm font-bold">2</span>
                Authorization & Disclosures
              </CardTitle>
              <CardDescription>
                Review and authorize AICreditPilot to act on your behalf.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAuthSubmit} className="space-y-6">
                {/* Disclosures */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Important Disclosures
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                      No guaranteed score increases. Results vary based on individual circumstances.
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                      This is an educational and assistance-based service.
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                      You may cancel your subscription at any time.
                    </li>
                  </ul>
                </div>

                {/* Authorization Text */}
                <div className="bg-teal-50 rounded-xl p-4 border border-teal-100">
                  <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <FileSignature className="h-5 w-5 text-teal-600" />
                    Authorization Agreement
                  </h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    "I authorize AICreditPilot to prepare and send dispute correspondence to credit 
                    bureaus on my behalf using the information I provide. I understand that AICreditPilot 
                    will act as my authorized representative in communicating with Experian, Equifax, and 
                    TransUnion regarding inaccurate items on my credit report."
                  </p>
                </div>

                {/* Consent Checkbox */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="consent"
                    checked={authData.consent_checkbox}
                    onCheckedChange={(checked) => setAuthData(prev => ({ ...prev, consent_checkbox: checked }))}
                    data-testid="consent-checkbox"
                  />
                  <Label htmlFor="consent" className="text-sm text-slate-700 leading-relaxed cursor-pointer">
                    I have read and agree to the authorization agreement above. I understand the disclosures 
                    and authorize AICreditPilot to send dispute letters on my behalf.
                  </Label>
                </div>

                {/* E-Signature */}
                <div className="space-y-2">
                  <Label htmlFor="e_signature">Electronic Signature</Label>
                  <Input
                    id="e_signature"
                    placeholder="Type your full legal name"
                    value={authData.e_signature}
                    onChange={(e) => setAuthData(prev => ({ ...prev, e_signature: e.target.value }))}
                    className="h-11"
                    data-testid="esignature-input"
                  />
                  <p className="text-xs text-slate-500">
                    By typing your name, you agree this constitutes your legal electronic signature.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-teal h-11"
                  disabled={loading || !authData.consent_checkbox}
                  data-testid="authorize-submit-btn"
                >
                  {loading ? 'Authorizing...' : 'Authorize & Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Plan Selection */}
        {step === 3 && (
          <Card className="border-slate-200 shadow-lg" data-testid="plan-step">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <span className="w-8 h-8 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-sm font-bold">3</span>
                Choose Your Plan
              </CardTitle>
              <CardDescription>
                Select a plan to complete your setup.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Free Plan */}
              <div 
                className="border border-slate-200 rounded-xl p-6 hover:border-slate-300 transition-colors cursor-pointer"
                onClick={() => handlePlanSelect('free')}
                data-testid="select-free-plan"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Manrope' }}>Free Plan</h3>
                    <p className="text-2xl font-bold text-slate-900">$0<span className="text-sm font-normal text-slate-500">/month</span></p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-600 mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-slate-400" />
                    Credit insights & education
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-slate-400" />
                    Manual letter download
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-slate-400" />
                    Basic support
                  </li>
                </ul>
                <Button 
                  variant="outline" 
                  className="w-full"
                  disabled={loading}
                  data-testid="free-plan-btn"
                >
                  Continue with Free
                </Button>
              </div>

              {/* Premium Plan */}
              <div 
                className="border-2 border-teal-500 rounded-xl p-6 bg-teal-50/30 cursor-pointer"
                onClick={() => handlePlanSelect('premium')}
                data-testid="select-premium-plan"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Manrope' }}>Premium Plan</h3>
                      <span className="bg-teal-600 text-white text-xs px-2 py-0.5 rounded-full">Recommended</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">$29.99<span className="text-sm font-normal text-slate-500">/month</span></p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-slate-700 mb-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                    <strong>Automatic dispute sending</strong>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                    Monthly automation cycles
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                    AI Credit Coach
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                    Real-time monitoring & alerts
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-teal-600" />
                    Priority support
                  </li>
                </ul>
                <Button 
                  className="w-full btn-teal h-11"
                  disabled={loading}
                  data-testid="premium-plan-btn"
                >
                  {loading ? 'Processing...' : 'Start Premium'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
